import { DsButton } from '@frontends/ds';
import { type ReactElement, useEffect, useEffectEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import Spinner from '@/components/Spinner.tsx';
import { useCompleteLogin } from '@/hooks/useCompleteLogin.tsx';
import { useEntityConfig } from '@/hooks/useEntityConfig.tsx';
import axios from '@/lib/axios.ts';
import { type SessionResponse } from '@/types/Auth.ts';
import { crmOriginOf, markEmbedSession, postToCrm, readCrmMessage, safeReturnTo } from '@/utils/b2coreEmbed.ts';

const HANDSHAKE_RESEND_INTERVAL_MS = 300;
const TOKEN_WAIT_MS = 15_000;

type EmbedStatus = 'connecting' | 'failed';

async function exchangeCrmToken(token: string): Promise<string | null> {
  try {
    const { data } = await axios.post<SessionResponse>(
      '/oauth/b2core/embed',
      { token },
      {
        withCredentials: true,
        headers: { 'X-Trace-ID': uuidv4() },
      },
    );

    return data.access_token || null;
  } catch {
    return null;
  }
}

const B2CoreEmbed = (): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const config = useEntityConfig();
  const completeLogin = useCompleteLogin();
  const crmOrigin = crmOriginOf(config.VITE_CLIENT_PORTAL_URL);
  const isInIframe = window.parent !== window;
  const canHandshake = crmOrigin !== null && isInIframe;
  const returnTo = safeReturnTo(searchParams.get('return_to'));
  const [status, setStatus] = useState<EmbedStatus>(canHandshake ? 'connecting' : 'failed');
  const [handshakeAttempt, setHandshakeAttempt] = useState(0);

  const signIn = useEffectEvent(async (accessToken: string): Promise<void> => {
    markEmbedSession();
    await completeLogin(accessToken, { silent: true });
    void navigate(returnTo, { replace: true });
  });

  useEffect(() => {
    if (crmOrigin === null || !isInIframe) {
      return;
    }

    let isActive = true;
    let isAwaitingToken = true;
    let isCrmListening = false;

    const sendPair = (): void => {
      postToCrm({ type: 'embed-iframe-ready' }, crmOrigin);
      postToCrm({ type: 'embed-request-jwt-token' }, crmOrigin);
    };

    const resendTimer = window.setInterval(sendPair, HANDSHAKE_RESEND_INTERVAL_MS);

    const fail = (): void => {
      window.clearInterval(resendTimer);
      window.clearTimeout(tokenTimer);
      isAwaitingToken = false;
      setStatus('failed');
    };

    const tokenTimer = window.setTimeout(fail, TOKEN_WAIT_MS);

    const exchange = async (token: string): Promise<void> => {
      const accessToken = await exchangeCrmToken(token);

      if (!isActive) {
        return;
      }

      if (accessToken === null) {
        fail();

        return;
      }

      await signIn(accessToken);
    };

    const handleMessage = (event: MessageEvent): void => {
      const message = readCrmMessage(event, crmOrigin);

      if (message === null) {
        return;
      }

      if (!isCrmListening) {
        isCrmListening = true;
        window.clearInterval(resendTimer);
      }

      if (!isAwaitingToken) {
        return;
      }

      if (message.type === 'embed-jwt-token') {
        isAwaitingToken = false;
        window.clearTimeout(tokenTimer);
        void exchange(message.token);
      }

      if (message.type === 'embed-jwt-token-error') {
        fail();
      }
    };

    window.addEventListener('message', handleMessage);
    sendPair();

    return () => {
      isActive = false;
      window.removeEventListener('message', handleMessage);
      window.clearInterval(resendTimer);
      window.clearTimeout(tokenTimer);
    };
  }, [crmOrigin, isInIframe, handshakeAttempt]);

  const handleRetry = (): void => {
    setStatus('connecting');
    setHandshakeAttempt((value) => value + 1);
  };

  if (status === 'connecting') {
    return (
      <div role="status" className="relative">
        <Spinner />
        <p className="absolute inset-x-0 top-1/2 mt-10 px-4 text-center text-base text-textSecondary">
          {t('embed_connecting')}
        </p>
      </div>
    );
  }

  return (
    <div className="flex min-h-svh items-center justify-center px-4 md:px-6">
      <div className="flex w-full max-w-sm flex-col items-center gap-4 text-center">
        <h1 className="text-2xl font-medium">{t('embed_failed_title')}</h1>
        <p className="text-base text-textSecondary">{t('embed_failed_desc')}</p>
        {canHandshake && (
          <DsButton size="lg" className="w-full" onPress={handleRetry}>
            {t('retry')}
          </DsButton>
        )}
      </div>
    </div>
  );
};

export default B2CoreEmbed;
